import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';

const { get, post } = erpApi();
const baseUrl = `${urlBackend}api/v1/mfa-admin`;

// =========================================================
// Tipos
// =========================================================

export type MfaEstadoLista =
  | 'todos'
  | 'con'
  | 'sin'
  | 'bloqueado'
  | 'forzados';

export interface MfaUsuarioRow {
  id: number;
  username: string;
  email: string | null;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_superuser: boolean;
  es_usuario_integracion: boolean;
  mfa_habilitado: boolean;
  mfa_metodo: 'app' | 'email' | null;
  mfa_intentos: number;
  mfa_bloqueado: boolean;
  mfa_obligatorio: boolean;
  mfa_grace_until: string | null;
  mfa_ultimo_uso: string | null;
  tiene_backup_codes: boolean;
  cantidad_backup_codes: number;
  grupos: string[];
}

export interface MfaListadoResp {
  count: number;
  page: number;
  page_size: number;
  numero_paginas: number;
  data: MfaUsuarioRow[];
}

export interface MfaListadoParams {
  q?: string;
  estado?: MfaEstadoLista;
  metodo?: 'app' | 'email' | '';
  integracion?: '0' | '1' | '';
  page?: number;
  page_size?: number;
}

export interface MfaPolitica {
  obligatorio_global: boolean;
  grupos_obligatorios: number[];
  dias_grace: number;
  fecha_modificacion: string;
  actualizado_por: string | null;
  grupos_disponibles: { id: number; name: string }[];
}

export interface MfaMetricas {
  total_usuarios_aplicables: number;
  activos: number;
  sin_2fa: number;
  porcentaje_cobertura: number;
  bloqueados: number;
  forzados: number;
  usuarios_integracion: number;
  por_metodo: { dos_factor_metodo: string | null; total: number }[];
  eventos_ultimos_30_dias: { evento: string; total: number }[];
}

export interface MfaEvento {
  id: number;
  evento: string;
  metodo: string | null;
  ip: string | null;
  fecha_creacion: string;
  detalle: Record<string, unknown> | null;
  usuario: string | null;
  usuario_id: number | null;
  admin: string | null;
}

export interface MfaBitacoraResp {
  count: number;
  page: number;
  page_size: number;
  numero_paginas: number;
  data: MfaEvento[];
}

export interface MfaBitacoraParams {
  evento?: string;
  user_id?: number;
  desde?: string;
  hasta?: string;
  page?: number;
  page_size?: number;
}

// =========================================================
// Endpoints
// =========================================================

const buildQs = <T extends object>(params: T) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export const fetchMfaListado = async (
  params: MfaListadoParams = {}
): Promise<MfaListadoResp> => {
  const res = await get<MfaListadoResp>(
    `${baseUrl}/usuarios/${buildQs(params)}`,
    true
  );
  return res.data;
};

export const fetchMfaDetalle = async (userId: number) => {
  const res = await get<MfaUsuarioRow & { eventos_recientes: MfaEvento[] }>(
    `${baseUrl}/usuarios/${userId}/`,
    true
  );
  return res.data;
};

export const resetearMfa = async (userId: number, motivo?: string) => {
  const res = await post<MfaUsuarioRow>(
    `${baseUrl}/usuarios/${userId}/resetear/`,
    { motivo: motivo ?? '' },
    true
  );
  return res.data;
};

export const desbloquearMfa = async (userId: number) => {
  const res = await post<MfaUsuarioRow>(
    `${baseUrl}/usuarios/${userId}/desbloquear/`,
    {},
    true
  );
  return res.data;
};

export const regenerarBackupAdmin = async (userId: number) => {
  const res = await post<{ backup_codes: string[] }>(
    `${baseUrl}/usuarios/${userId}/regenerar-backup/`,
    {},
    true
  );
  return res.data;
};

export const forzarMfa = async (
  userId: number,
  obligatorio: boolean,
  diasGrace?: number
) => {
  const res = await post<MfaUsuarioRow>(
    `${baseUrl}/usuarios/${userId}/forzar/`,
    { obligatorio, dias_grace: diasGrace ?? null },
    true
  );
  return res.data;
};

export const asignarGrace = async (
  userId: number,
  payload: { dias?: number; limpiar?: boolean; vencer_ahora?: boolean }
) => {
  const res = await post<MfaUsuarioRow>(
    `${baseUrl}/usuarios/${userId}/grace/`,
    payload,
    true
  );
  return res.data;
};

export const fetchMfaPolitica = async (): Promise<MfaPolitica> => {
  const res = await get<MfaPolitica>(`${baseUrl}/politica/`, true);
  return res.data;
};

export const guardarMfaPolitica = async (payload: Partial<MfaPolitica>) => {
  const res = await post<MfaPolitica>(
    `${baseUrl}/politica/set/`,
    payload,
    true
  );
  return res.data;
};

export const fetchMfaMetricas = async (): Promise<MfaMetricas> => {
  const res = await get<MfaMetricas>(`${baseUrl}/metricas/`, true);
  return res.data;
};

export const fetchMfaBitacora = async (
  params: MfaBitacoraParams = {}
): Promise<MfaBitacoraResp> => {
  const res = await get<MfaBitacoraResp>(
    `${baseUrl}/bitacora/${buildQs(params)}`,
    true
  );
  return res.data;
};

// =========================================================
// React Query hooks
// =========================================================

export const MFA_ADMIN_QUERY_KEYS = {
  LISTADO: 'mfa-admin-listado',
  DETALLE: 'mfa-admin-detalle',
  POLITICA: 'mfa-admin-politica',
  METRICAS: 'mfa-admin-metricas',
  BITACORA: 'mfa-admin-bitacora',
};

export const useMfaListado = (params: MfaListadoParams) =>
  useQuery({
    queryKey: [MFA_ADMIN_QUERY_KEYS.LISTADO, params],
    queryFn: () => fetchMfaListado(params),
  });

export const useMfaDetalle = (userId: number | null) =>
  useQuery({
    queryKey: [MFA_ADMIN_QUERY_KEYS.DETALLE, userId],
    queryFn: () => fetchMfaDetalle(userId as number),
    enabled: !!userId,
  });

export const useMfaPolitica = () =>
  useQuery({
    queryKey: [MFA_ADMIN_QUERY_KEYS.POLITICA],
    queryFn: fetchMfaPolitica,
    staleTime: 30_000,
  });

export const useMfaMetricas = () =>
  useQuery({
    queryKey: [MFA_ADMIN_QUERY_KEYS.METRICAS],
    queryFn: fetchMfaMetricas,
    staleTime: 60_000,
  });

export const useMfaBitacora = (params: MfaBitacoraParams) =>
  useQuery({
    queryKey: [MFA_ADMIN_QUERY_KEYS.BITACORA, params],
    queryFn: () => fetchMfaBitacora(params),
  });

const invalidarTodos = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.LISTADO] });
  qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.DETALLE] });
  qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.METRICAS] });
  qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.BITACORA] });
};

export const useResetearMfa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, motivo }: { userId: number; motivo?: string }) =>
      resetearMfa(userId, motivo),
    onSuccess: () => invalidarTodos(qc),
  });
};

export const useDesbloquearMfa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => desbloquearMfa(userId),
    onSuccess: () => invalidarTodos(qc),
  });
};

export const useRegenerarBackupAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => regenerarBackupAdmin(userId),
    onSuccess: () => invalidarTodos(qc),
  });
};

export const useForzarMfa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      obligatorio,
      diasGrace,
    }: {
      userId: number;
      obligatorio: boolean;
      diasGrace?: number;
    }) => forzarMfa(userId, obligatorio, diasGrace),
    onSuccess: () => invalidarTodos(qc),
  });
};

export const useAsignarGrace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dias,
      limpiar,
      vencer_ahora,
    }: {
      userId: number;
      dias?: number;
      limpiar?: boolean;
      vencer_ahora?: boolean;
    }) => asignarGrace(userId, { dias, limpiar, vencer_ahora }),
    onSuccess: () => invalidarTodos(qc),
  });
};

export const useGuardarMfaPolitica = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<MfaPolitica>) => guardarMfaPolitica(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.POLITICA] });
      qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.METRICAS] });
      qc.invalidateQueries({ queryKey: [MFA_ADMIN_QUERY_KEYS.BITACORA] });
    },
  });
};
